import React, { useState, useRef, useEffect } from "react";
import { ProgressBar } from "primereact/progressbar";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Dropdown } from "primereact/dropdown";
import axios from "../../../../util/axiosconfig";
import { Check, RotateCcw } from "lucide-react";
import CustomSpinner from "../../../../util/CustomSpinner";
import {
  showError,
  showSuccess,
  showWarning,
} from "../../../../util/ShowStatus";
import Filter from "./filter";

interface UploadStatus {
  status: "uploading" | "uploaded" | "failed" | null;
  message?: string;
}

interface ProcessingStatus {
  status: "processing" | "processed" | "failed" | null;
}

interface DownloadingStatus {
  status: "downloading" | "downloaded" | "failed" | null;
}

interface ReportDetail {
  reportID: number;
  tableName: string;
  spName: string;
  parameters: string;
  reportName: string;
  reportWithExtension: string;
}

export type FilterValues = {
  startDate: string | null;
  endDate: string | null;
  dma: string | null;
  location: string | null;
  area: string | null;
};

const GenerateReport = () => {
  const [fileName, setFileName] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>({
    status: null,
  });
  const [downloadStatus, setDownloadStatus] = useState<DownloadingStatus>({
    status: null,
  });
  const [processingStatus, setProcessingStatus] = useState<ProcessingStatus>({
    status: null,
  });
  const [showUploadReport, setShowUploadReport] = useState<boolean>(true);
  const [showDownloadReport, setShowDownloadReport] = useState<boolean>(true);
  const [excelSheetTypes, setExcelSheetTypes] = useState<string[]>([]);
  const [reportDetails, setReportDetails] = useState<ReportDetail[]>([]);
  const [filterValues, setFilterValues] = useState<FilterValues>({
    startDate: null,
    endDate: null,
    dma: null,
    location: null,
    area: null,
  });

  const [selectedUploadReport, setSelectedUploadReport] = useState<
    string | null
  >(null);
  const [selectedDownloadReport, setSelectedDownloadReport] =
    useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useRef<Toast>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null); // Reference for polling interval
  const isPollingRef = useRef<boolean>(false); // Reference to track if a polling request is in progress

  const fetchExcelType = async () => {
    try {
      const response = await axios.get("admin/populate-type-table");
      setExcelSheetTypes(
        response.data.result.map((doc: any) => doc.nameOfDocument)
      );
    } catch (error) {
      console.error(error);
    }
  };

  const fetchReportDetails = async () => {
    try {
      const reponse = await axios.get("admin/report-details");
      setReportDetails(reponse.data.result);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchExcelType();
    fetchReportDetails();
  }, []);

  const onUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    setSelectedUploadReport(null);
    if (file && selectedUploadReport) {
      setFileName(file.name);
      setUploadProgress(0);
      setUploadStatus((prevStatus: any) => ({
        ...prevStatus,
        status: "uploading",
      }));
      setProcessingStatus((prevStatus: any) => ({
        ...prevStatus,
        status: "",
      }));
      const formData = new FormData();
      formData.append("file", file); // 'file' is the name expected by your backend
      formData.append("nameOfDocument", selectedUploadReport); // Append the selected document name
      try {
        const response = await axios.post("admin/upload-file", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent: any) => {
            const { loaded, total } = progressEvent;
            const percent = Math.floor((loaded * 100) / total);
            setUploadProgress(percent);
          },
        });

        if (response.status === 200) {
          if (!response.data.unique_key) {
            setUploadStatus((prevStatus: any) => ({
              ...prevStatus,
              status: "failed",
              message: response.data.Message,
            }));
            showError(
              `${response.data.Message}, Please upload correct file!`,
              toast
            );
            setUploadProgress(null);
            setFileName(null); // Clear file name
            if (fileInputRef.current) {
              fileInputRef.current.value = ""; // Clear the input field
            }
            setSelectedUploadReport(null);
            setShowUploadReport(true);
          } else {
            setUploadStatus((prevStatus: any) => ({
              ...prevStatus,
              status: "uploaded",
            }));
            setProcessingStatus((prevStatus: any) => ({
              ...prevStatus,
              status: "processing",
            }));
            showSuccess("File processing, please wait!", toast);
            setUploadProgress(null);
            startPolling(response.data.unique_key);
          }
        }
      } catch (error) {
        console.error(error);
        showError("An error occurred during file upload.", toast);
        setUploadStatus((prevStatus: any) => ({
          ...prevStatus,
          status: "failed",
        }));
        setUploadProgress(null);
      }
    }
  };

  const startPolling = (uniqueKey: string) => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    pollingIntervalRef.current = setInterval(async () => {
      if (isPollingRef.current) return; // Exit if a request is already in progress
      isPollingRef.current = true; // Set polling in progress

      try {
        const response = await axios.get(
          `admin/processed-files?unique_key=${uniqueKey}`
        );

        if (response.data.result) {
          const allSuccess = response.data.result.every(
            (item: any) =>
              item.status === "SUCCESS" && item.error_message === null
          );

          if (allSuccess && response.data.result.length !== 0) {
            showSuccess("Your file has been successfully processed!", toast);
            setProcessingStatus((prevStatus: any) => ({
              ...prevStatus,
              status: "processed",
            }));
            setFileName(null); // Clear file name
            if (fileInputRef.current) {
              fileInputRef.current.value = ""; // Clear the input field
            }
            setSelectedUploadReport(null);
            setShowUploadReport(true);
            clearInterval(pollingIntervalRef.current!);
          } else {
            const hasError = response.data.result.some(
              (item: any) => item.error_message
            );
            if (hasError || response.data.result.length === 0) {
              setProcessingStatus((prevStatus: any) => ({
                ...prevStatus,
                status: "failed",
              }));
              showError(
                "Your file was not processed, please upload it again.",
                toast
              );
              setFileName(null); // Clear file name
              if (fileInputRef.current) {
                fileInputRef.current.value = ""; // Clear the input field
              }
              setSelectedUploadReport(null);
              setShowUploadReport(true);
              clearInterval(pollingIntervalRef.current!);
            }
          }
        }
      } catch (error: any) {
        clearInterval(pollingIntervalRef.current!);
        setProcessingStatus((prevStatus: any) => ({
          ...prevStatus,
          status: "failed",
        }));
        showError(error.message, toast);
      } finally {
        isPollingRef.current = false; // Reset polling status after request completes
      }
    }, 2000); // Poll every 2 seconds
  };
  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
    setShowUploadReport(false);
    setProcessingStatus((prevStatus: any) => ({
      ...prevStatus,
      status: "",
    }));
    setUploadStatus((prevStatus: any) => ({
      ...prevStatus,
      status: "",
    }));
  };

  const handleDownload = async () => {
    const params: any = {
      spName: selectedDownloadReport && selectedDownloadReport.spName,
      startDate: filterValues.startDate,
      endDate: filterValues.endDate,
      p_DMA: filterValues.dma === "" ? null : filterValues.dma,
      p_Location: filterValues.location === "" ? null : filterValues.location,
      p_Area: filterValues.area === "" ? null : filterValues.area,
    };

    if (!selectedDownloadReport) return; // Ensure a report is selected
    setShowDownloadReport(false);
    setDownloadStatus((prevStatus: any) => ({
      ...prevStatus,
      status: "downloading",
    }));
    try {
      const response = await axios.get(`admin/report`, {
        params,
        responseType: "blob", // Important for binary data
      });

      if (response.data.type === "application/json") {
        setDownloadStatus((prevStatus: any) => ({
          ...prevStatus,
          status: "failed",
        }));
        showWarning(
          `Report not found, Please retry downloading with different filters!`,
          toast
        );
        setShowDownloadReport(true);
      } else {
        // Create a blob link to download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `${selectedDownloadReport.reportWithExtension}`
        ); // Set the file name

        // Append to body and trigger download
        document.body.appendChild(link);
        link.click();

        // Clean up
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);
        setDownloadStatus((prevStatus: any) => ({
          ...prevStatus,
          status: "downloaded",
        }));
        showSuccess(
          `Downloading ${selectedDownloadReport.reportWithExtension}`,
          toast
        );
        setShowDownloadReport(true);
      }
    } catch (error: any) {
      setDownloadStatus((prevStatus: any) => ({
        ...prevStatus,
        status: "failed",
      }));
      setShowDownloadReport(true);
      if (error.response.status === 500) {
        showError(`Internal Server Error, please try again!`, toast);
      } else {
        showError("An error occurred while downloading the report.", toast);
      }
    }
  };

  // Full display for options (no truncation in dropdown list)
  const reportOptionTemplate = (option: { label: string; value: string }) => {
    return (
      <span>
        {option.label
          .replace(/_/g, " ") // Replace underscores with spaces
          .split(" ") // Split into words
          .map(
            (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          ) // Capitalize first letter
          .join(" ")}
      </span>
    );
  };

  // Truncated display only for the selected item in the input box
  const reportValueTemplate = (option: { label: string; value: string }) => {
    return (
      <span className="tw-truncate tw-block tw-max-w-full">
        {option
          ? option.label
              .replace(/_/g, " ") // Replace underscores with spaces
              .split(" ") // Split into words
              .map(
                (word) =>
                  word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
              ) // Capitalize first letter
              .join(" ")
          : "Select a report"}
      </span>
    );
  };

  return (
    <div className="tw-flex tw-flex-col tw-m-6 tw-mt-0 tw-p-4">
      <Toast ref={toast} />

      {/* Upload Section */}
      <div className="tw-mb-6 tw-mt-16 tw-p-6 tw-border-2 tw-border-gray tw-bg-[#EEEDEB] tw-shadow-sm tw-rounded-lg">
        <h3 className="tw-text-lg tw-font-bold tw-mb-4">Upload Excel File</h3>
        <div className="tw-w-full tw-flex tw-justify-between tw-items-center tw-mb-4">
          <div className="tw-flex tw-items-center tw-space-x-4 tw-mb-0 ">
            <Dropdown
              value={selectedUploadReport}
              options={excelSheetTypes}
              onChange={(e) => setSelectedUploadReport(e.value)}
              optionLabel="name"
              placeholder="Select excel sheet type"
              className="tw-w-[320px] tw-bg-gray-100 tw-border-gray-400 tw-border-solid tw-rounded-md tw-border"
              disabled={!showUploadReport}
              showClear
            />

            <input
              ref={fileInputRef}
              type="file"
              accept=".xls,.xlsx"
              onChange={onUpload}
              style={{ display: "none" }}
              disabled={!selectedUploadReport}
            />
            <Button
              label="Upload"
              icon="pi pi-upload"
              onClick={handleUploadClick}
              className="p-button-primary tw-text-white tw-bg-[#2a4870] tw-p-1 tw-pr-[23px] tw-pl-[23px] hover:tw-bg-[#182c44] focus:tw-shadow-none"
              raised
              disabled={!selectedUploadReport}
            />
          </div>
          {fileName && (
            <div className="tw-mr-4 tw-flex-grow tw-ml-[40px]">
              <p className="tw-text-gray-700 tw-text-lg tw-font-semibold">
                {fileName}
              </p>
            </div>
          )}
        </div>
        {uploadProgress !== null && (
          <div className="tw-w-full">
            <ProgressBar
              mode="indeterminate"
              value={uploadProgress}
              style={{ height: "6px" }}
              color="#2a4870"
            />
          </div>
        )}
      </div>

      {uploadStatus.status === "uploading" && (
        <div className="tw-text-red-500 tw-flex tw-items-center tw-container">
          <span className="tw-mr-2">Uploading...</span>
          <div className="spinner">
            <CustomSpinner spinnerColour="tw-border-t-red-500" />
          </div>
        </div>
      )}
      {uploadStatus.status === "uploaded" && (
        <div className="tw-text-green-500 tw-flex">
          <span className="tw-mr-[22px]"> Uploaded</span>
          <Check />
        </div>
      )}
      {uploadStatus.status === "failed" && (
        <div className="tw-text-red-500 tw-flex">
          <span className="tw-mr-4">File not uploaded, retry uploading</span>
          <RotateCcw />
        </div>
      )}

      {processingStatus.status === "processing" &&
        uploadStatus.status !== "failed" && (
          <div className="tw-text-red-500 tw-flex tw-items-center tw-container">
            <span className="tw-mr-2">Processing...</span>
            <div className="spinner">
              <CustomSpinner spinnerColour="tw-border-t-red-500" />
            </div>
          </div>
        )}
      {processingStatus.status === "processed" &&
        uploadStatus.status !== "failed" && (
          <div className="tw-text-green-500 tw-flex">
            <span className="tw-mr-4"> Processed</span>
            <Check />
          </div>
        )}
      {processingStatus.status === "failed" &&
        uploadStatus.status !== "failed" && (
          <div className="tw-text-red-500 tw-flex">
            <span className="tw-mr-4">Processing failed, Retry uploading!</span>
            <RotateCcw />
          </div>
        )}

      {/* Reports Section */}
      <div className="tw-p-6 tw-bg-gray-100 tw-shadow-md tw-rounded-lg tw-mt-20">
        <h3 className="tw-text-xl tw-font-bold tw-mb-4">Available Reports</h3>
        <div className="tw-flex tw-flex-col tw-justify-start tw-space-x-4 tw-mb-0 ">
          <Filter
            setFilterValues={setFilterValues}
            filterValues={filterValues}
          />
          <div className="tw-mt-4">
            <Dropdown
              value={selectedDownloadReport}
              disabled={!showDownloadReport}
              options={
                reportDetails?.map((report: any) => ({
                  label: report.reportName, // Displayed text
                  value: report, // Underlying value
                })) || []
              }
              onChange={(e) => setSelectedDownloadReport(e.value)}
              optionLabel="label"
              placeholder="Select a Report"
              className="tw-w-[340px] tw-border-gray-400 tw-border-solid tw-rounded-md tw-border tw-bg-gray-100"
              itemTemplate={reportOptionTemplate}
              valueTemplate={reportValueTemplate}
              showClear
              filter
            />
            <Button
              label="Download"
              icon="pi pi-download"
              onClick={handleDownload}
              className="p-button-primary focus:tw-shadow-none tw-text-white tw-bg-[#2a4870] tw-p-1 tw-pr-3 tw-pl-3 tw-ml-4 tw-mr-[-10px] tw-mb-2 hover:tw-bg-[#182c44]"
              raised
              disabled={!selectedDownloadReport || !filterValues.endDate}
            />
          </div>
          {downloadStatus.status === "downloading" && (
            <div className="tw-absolute tw-pl-[495px] tw-mt-[105px]">
              <CustomSpinner spinnerColour="tw-border-t-[#182c44]" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GenerateReport;
